/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import TableHeadCell from '@mdsol/lego/lib/TableHeaderCell'
import { useServiceContext } from '../../root'
import { Link } from 'react-router'
import { AclLevel, FsiType } from '../../constants/fsi'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import { SEARCH_TYPE_PARSE, SEARCH_TYPE_V2 } from '../../constants/search'
import type { SearchHit } from './types'
import LoadingSpinner from '../../components/LoadingSpinner'
import FileIcon from '../../components/FileIcon'
import ActionsMenu from '../../components/shared/ActionsMenu'
import { useFsiActionMenu } from '../../hooks/useFsiActionMenu'
import { formatName } from '../../services/collections/filterService'

interface SearchTableProps {
  showFullTable?: boolean
  onShowMoreClick?: () => void
  onBackClick?: () => void
  isLoading?: boolean
  results?: SearchHit[]
  totalItems: number
  resultType: string
}

export const SearchTable: React.FC<SearchTableProps> = ({
  showFullTable = false,
  onShowMoreClick,
  onBackClick,
  isLoading = false,
  results = [],
  totalItems,
  resultType
}) => {
  const {
    service: {
      filter: { translate }
    }
  } = useServiceContext()

  const headRow = (
    <tr className="has-actions">
      <TableHeadCell>{translate('DOCUMENT_NAME')}</TableHeadCell>
      <TableHeadCell>{translate('FORMAT')}</TableHeadCell>
      <TableHeadCell>{translate('CREATED_ON')}</TableHeadCell>
      <TableHeadCell>{translate('TABLE_TH_UPDATED_ON')}</TableHeadCell>
      <TableHeadCell>{translate('ACTIONS')}</TableHeadCell>
    </tr>
  )

  /**
   * Render excerpts (highlights) for content and docUID search results
   */
  const renderExcerpts = (row: SearchHit) => {
    if (resultType !== SEARCH_TYPE_V2.docUID && resultType !== SEARCH_TYPE_V2.content) return null

    const fieldKey = SEARCH_TYPE_PARSE[resultType as keyof typeof SEARCH_TYPE_PARSE]
    const highlights = row.highlights?.[fieldKey as keyof typeof row.highlights]

    if (!highlights || highlights.length === 0) return null

    return (
      <div className="excerpts">
        <span className="search-meta-black">
          {translate(resultType === SEARCH_TYPE_V2.docUID ? 'DOCUMENT_IDENTIFIER:' : 'SEARCH_CONTENT_EXCERPT(S)')}{' '}
        </span>
        {highlights.map((highlight: any, index: number) => (
          <span key={index} className="search-meta-gray">
            {resultType === SEARCH_TYPE_V2.content && '...'}
            <span dangerouslySetInnerHTML={{ __html: highlight.value }} />
            {resultType === SEARCH_TYPE_V2.content && `...${index + 1 < highlights.length ? ', ' : ''}`}
          </span>
        ))}
      </div>
    )
  }

  /**
   * Table Menu component for each row
   */
  const TableMenu: React.FC<{ item: any }> = ({ item }) => {
    const { items, setIsOpen } = useFsiActionMenu(item, {})

    if (items.length === 0) {
      items.push({ value: 'ACTION_MENU_NO_ACTION_AVAILABLE', disabled: true })
    }

    return (
      <ActionsMenu
        menuId={`action-menu-browser-${item.id}`}
        onToggle={(open: boolean) => {
          setIsOpen(open)
        }}
        items={items}
      />
    )
  }

  /**
   * Render individual table row
   */
  const renderRow = (row: SearchHit) => {
    const fsi = row.fsi
    const canNavigate =
      fsi.type !== FsiType.PLACEHOLDER && !(fsi.acl === AclLevel.BROWSE && fsi.type !== FsiType.FOLDER) && fsi.valid

    const inWorkflow = fsi.workflow && fsi.workflow.length > 0

    return (
      <tr key={row.fileId}>
        <td className="name clipped-cell with-thumb">
          <div className="d-flex align-left">
            {/* Valid document indicator (green dot) - only for documents, not folders */}
            {fsi.type !== FsiType.FOLDER && (
              <span
                style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  marginRight: '5px',
                  flexShrink: 0,
                  backgroundColor: fsi.valid ? '#28a745' : '#dc3545'
                }}
              />
            )}

            {/* File/Folder Icon */}
            <FileIcon
              fileName={fsi.name || ''}
              fileType={fsi.type as 'FOLDER' | 'DOCUMENT'}
              isValid={fsi.valid || false}
              isObsolete={fsi.lifecycleState === 'OBSOLETE'}
              inWorkflow={inWorkflow}
              showValidityIndicator={false} // We're showing it separately above
            />

            <div className="ellipsis" title={fsi.name}>
              {canNavigate ? (
                <Link
                  to={
                    fsi.type === FsiType.FOLDER
                      ? `/browser/${fsi.id}?type=${fsi.systemFolderType}`
                      : `/show-file/${fsi.id}`
                  }
                >
                  {resultType === SEARCH_TYPE_V2.filename && row.highlights?.FILE_NAME?.[0]?.value ? (
                    <span dangerouslySetInnerHTML={{ __html: row.highlights.FILE_NAME[0].value }} />
                  ) : (
                    fsi.name
                  )}
                  {renderExcerpts(row)}
                </Link>
              ) : (
                <>
                  <span>{fsi.name}</span>
                  {renderExcerpts(row)}
                </>
              )}
            </div>

            {/* Document State Badge (Effective/Expired/Obsolete) */}
            {fsi.lifecycleState && fsi.lifecycleState !== 'EFFECTIVE' && (
              <span className={`badge badge-${fsi.lifecycleState.toLowerCase()}`} style={{ marginLeft: '8px' }}>
                {translate('STATE_' + fsi.lifecycleState)}
              </span>
            )}
          </div>
        </td>
        <td className="narrow whitespacenowrap">{fsi.name ? formatName(fsi.name) : '-'}</td>
        <td className="narrow updated">{fsi.createdAt ? new Date(fsi.createdAt).toLocaleDateString() : '-'}</td>
        <td className="narrow updated">{fsi.updatedAt ? new Date(fsi.updatedAt).toLocaleDateString() : '-'}</td>
        <td className="actions">{fsi.valid && <TableMenu item={fsi} />}</td>
      </tr>
    )
  }

  if (isLoading) {
    return <LoadingSpinner centered size={40} />
  }

  // Render full table mode with RcmTable and custom TableFooter
  if (showFullTable) {
    const TableFooter = onBackClick ? (
      <tr>
        <td colSpan={5} className="view-all">
          <a className="link-more" onClick={onBackClick}>
            {translate('SEARCH_BACK')}
          </a>
        </td>
      </tr>
    ) : null

    return (
      <div className={`search-${resultType}`}>
        <RcmTable
          HeadRow={headRow}
          Row={({ row }) => renderRow(row)}
          data={results || []}
          className="chita-table rcm-search-table"
          TableFooter={TableFooter}
        />
      </div>
    )
  }

  // Render preview mode with plain table and "Show More" button
  return (
    <div className={`search-${resultType}`}>
      <table className="table chita-table fsiTable search-filename">
        <thead>{headRow}</thead>
        <tbody>
          {results?.length ? (
            results.map(renderRow)
          ) : (
            <tr>
              <td colSpan={5}>{translate('NO_RESULTS')}</td>
            </tr>
          )}
        </tbody>
        {results?.length && !isLoading && totalItems > 10 ? (
          <tfoot>
            <tr key="show-more-row">
              <td colSpan={5} className="view-all">
                <a className="link-more" onClick={onShowMoreClick}>
                  {translate('SEE_MORE')}
                </a>
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  )
}
